import React, { useState, useEffect } from 'react'
import Seo from '../components/Seo';
import { useLocation } from 'react-router-dom';
import { CONFIG } from '../config';

const sections = [
  { id: 'rules', label: 'Rules & Honor Code' },
  { id: 'fee-waiver', label: 'Fee Waiver Policy' },
  { id: 'privacy', label: 'Privacy Policy' },
  { id: 'terms', label: 'Terms & Conditions' },
];

function RulesandRegulations() {
  const [activeSection, setActiveSection] = useState('rules');
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const sectionId = location.hash.slice(1);
      if (sections.find(s => s.id === sectionId)) {
        setActiveSection(sectionId);
      }
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      })).filter(s => s.element);

      for (const section of sectionElements) {
        const rect = section.element.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom > 150) {
          setActiveSection(section.id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  return (
    <div>
      <Seo
        title="Rules & Regulations"
        path="/rules-and-regulations"
        description="The official rules and regulations of the International Research Olympiad — conduct, scoring, and competition guidelines for all participants."
      />
      {/* Hero Section */}
      <div style={{ backgroundColor: "#f8f9fa", padding: "60px 0 50px 0" }}>
        <div className="container">
          <div style={{ maxWidth: "800px" }}>
            <h1 style={{
              fontSize: "clamp(2rem, 4vw, 2.5rem)",
              fontWeight: "700",
              color: "#1a1a2e",
              marginBottom: "16px",
              letterSpacing: "-0.02em"
            }}>
              Rules & Regulations
            </h1>
            <p style={{
              fontSize: "1.1rem",
              color: "#555",
              lineHeight: "1.7"
            }}>
              Please review our policies, rules, and terms before participating in the International Research Olympiad.
            </p>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: "50px 0 80px 0" }}>
        <div style={{ display: "flex", gap: "50px", flexWrap: "wrap" }}>
          {/* Sidebar Navigation */}
          <div style={{
            width: "220px",
            flexShrink: 0,
            position: "sticky",
            top: "100px",
            alignSelf: "flex-start",
            height: "fit-content"
          }}>
            <nav style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  style={{
                    background: activeSection === section.id ? "rgba(0, 77, 153, 0.08)" : "transparent",
                    border: "none",
                    borderLeft: activeSection === section.id ? "3px solid #004d99" : "3px solid transparent",
                    padding: "12px 16px",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    fontWeight: activeSection === section.id ? "600" : "500",
                    color: activeSection === section.id ? "#004d99" : "#555",
                    borderRadius: "0 8px 8px 0",
                    transition: "all 0.2s ease"
                  }}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: "300px", maxWidth: "800px" }}>
            {/* Rules & Honor Code */}
            <section id="rules" style={{ marginBottom: "60px" }}>
              <h2 style={{
                fontSize: "1.75rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "24px",
                paddingBottom: "12px",
                borderBottom: "2px solid #e8eef3"
              }}>
                IRO Rules & Honor Code
              </h2>

              {/* Full Rules */}
              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "28px",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e8eef3",
                marginBottom: "24px"
              }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "20px" }}>
                  Complete Rules
                </h3>
                <ul style={{ margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    All competition tasks, research, and submissions must be independently completed by the competitor. Assistance from mentors, teachers, family members, or any other external individuals is strictly prohibited.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    The use of generative AI, as well as other automated tools or software that significantly enhance research capabilities beyond standard means, is strictly prohibited. This includes but is not limited to, ChatGPT, chatbots, advanced data analysis tools, extensive code libraries, and any software that automates the research process.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    Competitors are prohibited from using VPNs, proxy servers, TOR networks, or any other technology that obscures or alters their real-time location, identity, or facilitates anonymous internet access during the competition.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    Direct or indirect communication with other competitors regarding any aspect of the competition is prohibited. This includes, but is not limited to, discussions on public forums, social media, or through intermediaries. The exchange of competition-related information in any form is strictly forbidden. Competitors must not share, distribute, or publish competition questions, problems, or materials with anyone, both during and after the competition, unless explicitly authorized by the organizers.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    Competitors must not exploit system vulnerabilities or technical glitches, or use any unfair means to gain access to restricted information, extend time limits, or otherwise breach the competition's integrity. This includes but is not limited to, the use of multiple logins, hacking, or manipulating the competition platform.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    The competition organizers will monitor compliance with these rules. Any violation will lead to immediate disqualification and potential further consequences. It is the responsibility of each competitor to adhere to these rules and uphold the spirit of fair competition.
                  </li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.7" }}>
                    All participants are expected to read and follow the Terms of Service (TOS). There is no exception to any of these rules.
                  </li>
                </ul>
              </div>

              {/* Abridged Rules */}
              <div style={{
                background: "rgba(0, 77, 153, 0.03)",
                borderRadius: "12px",
                padding: "28px",
                border: "1px solid rgba(0, 77, 153, 0.1)",
                marginBottom: "24px"
              }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#004d99", marginBottom: "20px" }}>
                  Quick Summary
                </h3>
                <ul style={{ margin: 0, paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>All competition and work during the IRO must be done by yourself and not with a team.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>The use of generative AI and AI tools is strictly prohibited.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Competitors must not use a VPN during competition.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Communicating with other competitors during and about the contest is prohibited.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>The use of multiple logins to access the contest is strictly prohibited.</li>
                </ul>
              </div>

              {/* Honor Code */}
              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "28px",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e8eef3",
                borderLeft: "4px solid #004d99"
              }}>
                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  Honor Code
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", margin: 0, fontStyle: "italic" }}>
                  As a participant in the International Research Olympiad, I pledge to uphold the highest standards of integrity, honesty, and ethical conduct. I commit to submitting only original work, giving proper attribution, and avoiding any form of plagiarism or dishonest behavior. I will respect collaboration rules, handle data transparently, and maintain the confidentiality of shared information. My conduct will be professional and respectful, and I will adhere to all competition rules. I accept responsibility for my actions, will report any violations, and embrace the principles of fair competition. I understand that any breach of this commitment may lead to consequences, including disqualification.
                </p>
              </div>
            </section>

            {/* Fee Waiver Policy */}
            <section id="fee-waiver" style={{ marginBottom: "60px" }}>
              <h2 style={{
                fontSize: "1.75rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "24px",
                paddingBottom: "12px",
                borderBottom: "2px solid #e8eef3"
              }}>
                IRO Fee Waiver Policy
              </h2>

              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "28px",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e8eef3"
              }}>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  At the International Research Olympiad (IRO), we are committed to providing equal opportunities for participation. We understand that financial constraints may pose challenges for some individuals. As part of our commitment to inclusivity, we offer a Fee Waiver Policy to eligible participants.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  How to Apply
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "16px" }}>
                  We strongly encourage applicants to attach documentation that shows you meet at least one of the following:
                </p>
                <ul style={{ margin: "0 0 24px 0", paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Your annual family income falls within the income eligibility guidelines set by the USDA Food and Nutrition Service for reduced price or free meals, or you are enrolled in the federal free or reduced price lunch program (FRPL).*</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You are currently experiencing financial hardship that prevents you from paying the registration fee, including unexpected medical bills or emergencies.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You have received or are eligible to receive an SAT or ACT fee waiver.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You have received or are eligible to receive a Common App fee waiver.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Your family receives public assistance.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You are enrolled in a federal, state, or local program that aids students from low-income families (e.g., GEAR UP, TRIO such as Upward Bound or others).</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You live in a federally subsidized public housing, a foster home or are homeless.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You are a ward of the state or an orphan.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You have received or are eligible to receive a Pell Grant.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>You can provide a supporting statement from a school official, college access counselor, financial aid officer, or community leader.</li>
                </ul>
                <p style={{ fontSize: "0.85rem", color: "#666", fontStyle: "italic", marginBottom: "24px" }}>
                  *You must enroll or be eligible for the FRPL. Students who attend schools where all students receive free lunch do not automatically qualify for an International Research Olympiad fee waiver.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  Approval Process
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  Fee waiver requests will be reviewed on a case-by-case basis. Approval for a fee waiver is based on the information provided, the available resources, and any other circumstances up to our sole discretion. Participants will be notified of the decision via email within 2 weeks. Fee waivers are never guaranteed. Fee waiver requests are accepted during each cycle's registration period; requests submitted after the published deadline will not be considered. If accepted, we will automatically register those students in the IRO.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  Important Notes
                </h3>
                <ul style={{ margin: "0 0 24px 0", paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "10px" }}>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Do your best to send an application as early as possible. If you submit too close to the application deadline, we may not have time to review your request.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Participants who are granted fee waivers will receive instructions on completing their registration without payment.</li>
                  <li style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.6" }}>Financial barriers should not hinder access to educational opportunities, and we strive to support all deserving participants.</li>
                </ul>

                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", margin: 0 }}>
                  For any questions or concerns regarding fee waivers, please contact us at <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} style={{ color: "#004d99", fontWeight: "600" }}>{CONFIG.CONTACT_EMAIL}</a>
                </p>
              </div>
            </section>

            {/* Privacy Policy */}
            <section id="privacy" style={{ marginBottom: "60px" }}>
              <h2 style={{
                fontSize: "1.75rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "24px",
                paddingBottom: "12px",
                borderBottom: "2px solid #e8eef3"
              }}>
                International Research Olympiad Privacy Policy
              </h2>

              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "28px",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e8eef3"
              }}>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "16px" }}>
                  The International Research Olympiad (the "Company") is committed to maintaining robust privacy protections for its users. Our Privacy Policy ("Privacy Policy") is designed to help you understand how we collect, use and safeguard the information you provide to us and to assist you in making informed decisions when using our Service.
                </p>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  For purposes of this Agreement, "Site" refers to the Company's website, which can be accessed at the International Research Olympiad. "Service" refers to the Company's services accessed via the Site, in which users can register to participate in the International Research Olympiad. The terms "we," "us," and "our" refer to the Company. "You" refers to you, as a user of our Site or our Service. By accessing our Site or our Service, you accept our Privacy Policy and Terms of Use, and you consent to our collection, storage, use, and disclosure of your Personal Information as described in this Privacy Policy.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  I. Information We Collect
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "20px" }}>
                  We collect "Non-Personal Information" and "Personal Information." Non-Personal Information includes information that cannot be used to personally identify you, such as anonymous usage data, general demographic information we may collect, referring/exit pages and URLs, platform types, preferences you submit, and preferences that are generated based on the data you submit and number of clicks. Personal Information includes your email, date of birth, full legal name, address, contact information, and anything additional to identify You, which you submit to us through the registration process at the Site.
                </p>

                <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "12px" }}>
                  1. Information Collected via Technology
                </h4>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "16px" }}>
                  To activate the Service you do not need to submit any Personal Information other than your email address. To use the Service thereafter, you do need to submit further Personal Information which may include your email, date of birth, full legal name, address, contact information, and anything additional to identify you. However, in an effort to improve the quality of the Service, we track information provided to us by your browser or by our software application when you view or use the Service, such as the website you came from (known as the "referring URL"), the type of browser you use, the device from which you connected to the Service, the time and date of access, and other information that does not personally identify you. We track this information using cookies, or small text files which include an anonymous unique identifier. Cookies are sent to a user's browser from our servers and are stored on the user's computer hard drive. Sending a cookie to a user's browser enables us to collect Non-Personal information about that user and keep a record of the user's preferences when utilizing our services, both on an individual and aggregate basis. For example, the Company may use cookies to collect the following information:
                </p>
                <ul style={{ margin: "0 0 16px 0", paddingLeft: "20px", display: "flex", flexDirection: "column", gap: "8px" }}>
                  <li style={{ fontSize: "0.95rem", color: "#555" }}>Internet Protocol (IP) Address</li>
                  <li style={{ fontSize: "0.95rem", color: "#555" }}>Device information</li>
                  <li style={{ fontSize: "0.95rem", color: "#555" }}>Browser information</li>
                </ul>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "20px" }}>
                  The Company may use both persistent and session cookies; persistent cookies remain on your computer after you close your session and until you delete them, while session cookies expire when you close your browser.
                </p>

                <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "12px" }}>
                  2. Information You Provide Us by Registering for an Account
                </h4>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "20px" }}>
                  In addition to the information provided automatically by your browser when you visit the Site, to become a subscriber to the Service you will need to create a personal profile. You can create a profile by registering with the Service and entering your email address, and creating a username and a password. By registering, you are authorizing us to collect, store, and use your email address in accordance with this Privacy Policy.
                </p>

                <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "12px" }}>
                  3. Children's Privacy
                </h4>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  The Site and the Service are not directed to anyone under the age of 13. The Site does not knowingly collect or solicit information from anyone under the age of 13, or allow anyone under the age of 13 to sign up for the Service. In the event that we learn that we have gathered personal information from anyone under the age of 13 without the consent of a parent or guardian, we will delete that information as soon as possible. If you believe we have collected such information, please contact us at <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} style={{ color: "#004d99", fontWeight: "600" }}>{CONFIG.CONTACT_EMAIL}</a>.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  II. How We Use and Share Information
                </h3>
                <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "12px" }}>
                  Personal Information
                </h4>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "16px" }}>
                  Except as otherwise stated in this Privacy Policy, we do not sell, trade, rent or otherwise share for marketing purposes your Personal Information with third parties without your consent. We do share Personal Information with vendors who are performing services for the Company, such as the servers for our email communications who are provided access to user's email address for purposes of sending emails from us. Those vendors use your Personal Information only at our direction and in accordance with our Privacy Policy. In general, the Personal Information you provide to us is used to help us communicate with you. For example, we use Personal Information to contact users in response to questions, solicit feedback from users, provide technical support, and inform users about promotional offers.
                </p>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "20px" }}>
                  We may share Personal Information with outside parties if we have a good-faith belief that access, use, preservation or disclosure of the information is reasonably necessary to meet any applicable legal process or enforceable governmental request; to enforce applicable Terms of Service, including investigation of potential violations; address fraud, security or technical concerns; or to protect against harm to the rights, property, or safety of our users or the public as required or permitted by law.
                </p>

                <h4 style={{ fontSize: "1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "12px" }}>
                  Non-Personal Information
                </h4>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "16px" }}>
                  In general, we use Non-Personal Information to help us improve the Service and customize the user experience. We also aggregate Non-Personal Information in order to track trends and analyze use patterns on the Site. This Privacy Policy does not limit in any way our use or disclosure of Non-Personal Information and we reserve the right to use and disclose such Non-Personal Information to our partners, advertisers and other third parties at our discretion.
                </p>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  In the event we undergo a business transaction such as a merger, acquisition by another company, or sale of all or a portion of our assets, your Personal Information may be among the assets transferred. You acknowledge and consent that such transfers may occur and are permitted by this Privacy Policy, and that any acquirer of our assets may continue to process your Personal Information as set forth in this Privacy Policy. If our information practices change at any time in the future, we will post the policy changes to the Site so that you may opt out of the new information practices. We suggest that you check the Site periodically if you are concerned about how your information is used.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  III. How We Protect Information
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  We implement security measures designed to protect your information from unauthorized access. Your account is protected by your account password and we urge you to take steps to keep your personal information safe by not disclosing your password and by logging out of your account after each use. We further protect your information from potential security breaches by implementing certain technological security measures including encryption, firewalls and secure socket layer technology. However, these measures do not guarantee that your information will not be accessed, disclosed, altered or destroyed by breach of such firewalls and secure server software. By using our Service, you acknowledge that you understand and agree to assume these risks.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  IV. Your Rights Regarding the Use of Your Personal Information
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  When we send a promotional communication to a user, the user can opt out of further promotional communications by following the unsubscribe instructions provided in each promotional e-mail. You can also indicate that you do not wish to receive marketing communications from us by contacting us at <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} style={{ color: "#004d99", fontWeight: "600" }}>{CONFIG.CONTACT_EMAIL}</a>. Please note that notwithstanding the promotional preferences you indicate by either unsubscribing or opting out by emailing us, we may continue to send you administrative emails including, for example, periodic updates to our Privacy Policy.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  V. Links to Other Websites
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  As part of the Service, we may provide links to or compatibility with other websites or applications. However, we are not responsible for the privacy practices employed by those websites or the information or content they contain. This Privacy Policy applies solely to information collected by us through the Site and the Service. Therefore, this Privacy Policy does not apply to your use of a third party website accessed by selecting a link on our Site or via our Service. To the extent that you access or use the Service through or on another website or application, then the privacy policy of that other website or application will apply to your access or use of that site or application. We encourage our users to read the privacy statements of other websites before proceeding to use them.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  VI. Changes to Our Privacy Policy
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", marginBottom: "24px" }}>
                  The Company reserves the right to change this policy and our Terms of Service at any time. We will notify you of significant changes to our Privacy Policy by sending a notice to the primary email address specified in your account or by placing a prominent notice on our site. Significant changes will go into effect at least 7 days following such notification. Non-material changes or clarifications will take effect immediately. You should periodically check the Site and this privacy page for updates.
                </p>

                <h3 style={{ fontSize: "1.1rem", fontWeight: "600", color: "#1a1a2e", marginBottom: "16px" }}>
                  VII. Contact Us
                </h3>
                <p style={{ fontSize: "0.95rem", color: "#555", lineHeight: "1.8", margin: 0 }}>
                  If you have any questions regarding this Privacy Policy or the practices of this Site, please contact us by sending an email to <a href={`mailto:${CONFIG.CONTACT_EMAIL}`} style={{ color: "#004d99", fontWeight: "600" }}>{CONFIG.CONTACT_EMAIL}</a>. Last Updated: This Privacy Policy was last updated on January 20th, 2025.
                </p>
              </div>
            </section>

            {/* Terms & Conditions */}
            <section id="terms">
              <h2 style={{
                fontSize: "1.75rem",
                fontWeight: "600",
                color: "#1a1a2e",
                marginBottom: "24px",
                paddingBottom: "12px",
                borderBottom: "2px solid #e8eef3"
              }}>
                Terms and Conditions
              </h2>

              <div style={{
                background: "#fff",
                borderRadius: "12px",
                padding: "28px",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                border: "1px solid #e8eef3"
              }}>
                <iframe
                  title="Terms and Conditions"
                  src="https://docs.google.com/document/d/e/2PACX-1vTHZ-_OdNXn9k3IV0f4kFnzHv817BD43cphfRCvUogZpklu8M_VYRxlaYbEHEMxsXJIVA4C3vxfwceM/pub?embedded=true"
                  style={{
                    width: "100%",
                    height: "600px",
                    border: "none",
                    borderRadius: "8px"
                  }}
                />
                <p style={{ fontSize: "0.9rem", color: "#666", marginTop: "16px", marginBottom: 0 }}>
                  <a href="/terms-of-service" style={{ color: "#004d99", fontWeight: "600" }}>View full Terms of Service</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RulesandRegulations
